import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import { useMemo, useState } from 'react';
import packageJson from '../../../package.json';

const drawerWidth = 288;

type NavItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
};

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems: NavItem[] = useMemo(
    () => [
      { label: 'Overview', path: '/overview', icon: <DashboardOutlinedIcon /> },
      { label: 'Tenants', path: '/tenants', icon: <ApartmentOutlinedIcon /> },
      { label: 'Users', path: '/users', icon: <PeopleAltOutlinedIcon /> },
      { label: 'Audit log', path: '/audit', icon: <PolicyOutlinedIcon /> },
    ],
    []
  );

  const activeLabel = useMemo(() => {
    const match = navItems.find(
      (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
    );
    return match?.label ?? 'Console';
  }, [location.pathname, navItems]);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: 2.25,
          py: 2.5,
          background: 'linear-gradient(180deg, rgba(91,156,248,0.08) 0%, transparent 100%)',
        }}
      >
        <Stack direction="row" spacing={1.75} alignItems="center" sx={{ mb: 1 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, rgba(91,156,248,0.35) 0%, rgba(159,140,255,0.2) 100%)',
              border: '1px solid rgba(91,156,248,0.45)',
              display: 'grid',
              placeItems: 'center',
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: -0.5,
              color: 'primary.light',
              boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
            }}
            aria-hidden
          >
            IAM
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.02, lineHeight: 1.15 }}>
              x-IAM
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.72, display: 'block' }}>
              Identity & access
            </Typography>
          </Box>
        </Stack>
        <Typography variant="body2" sx={{ opacity: 0.78, pl: 0.25, fontWeight: 500 }}>
          Superadmin console
        </Typography>
      </Box>
      <Divider flexItem sx={{ opacity: 0.5 }} />
      <List sx={{ px: 1.25, py: 1.5, flex: 1 }}>
        {navItems.map((item) => {
          const selected =
            location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
          return (
            <ListItemButton
              key={item.path}
              selected={selected}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 1.15,
              }}
            >
              <ListItemIcon sx={{ minWidth: 42, color: selected ? 'primary.light' : 'text.secondary' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: selected ? 700 : 500,
                  fontSize: 15,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Divider flexItem sx={{ opacity: 0.5 }} />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" sx={{ opacity: 0.55, display: 'block', mb: 1.25, px: 0.5 }}>
          v{packageJson.version} · {import.meta.env.MODE}
        </Typography>
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          startIcon={<LoginOutlinedIcon />}
          onClick={() => {
            navigate('/login');
            setMobileOpen(false);
          }}
          sx={{
            borderColor: 'rgba(91,156,248,0.35)',
            color: 'primary.light',
            fontWeight: 600,
          }}
        >
          Sign-in screen
        </Button>
        <Typography variant="caption" sx={{ opacity: 0.5, display: 'block', mt: 1.25, px: 0.5, lineHeight: 1.45 }}>
          Authentication gate is off for UI review. Enable it again when wiring x-IAM tokens.
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', position: 'relative' }}>
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          backdropFilter: 'blur(14px) saturate(1.2)',
          backgroundColor: 'rgba(7, 11, 18, 0.72)',
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: 64, gap: 2 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen((v) => !v)}
            sx={{ mr: 1, display: { md: 'none' } }}
            aria-label="open menu"
          >
            <MenuIcon />
          </IconButton>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 800, letterSpacing: -0.02 }}>
              x-IAM
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.45, flexShrink: 0 }} component="span">
              /
            </Typography>
            <Typography
              variant="body1"
              sx={{
                opacity: 0.88,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              component="span"
            >
              {activeLabel}
            </Typography>
          </Stack>
          <Box sx={{ flex: 1 }} />
          <Chip
            label="UI preview · auth off"
            size="small"
            sx={{
              display: { xs: 'none', sm: 'flex' },
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 0.3,
              bgcolor: 'rgba(251, 191, 36, 0.12)',
              color: 'warning.light',
              border: '1px solid rgba(251, 191, 36, 0.35)',
            }}
          />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="Primary"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 3, md: 4 },
          mt: 8,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.35,
            backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse 70% 60% at 50% 0%, black, transparent)',
            zIndex: 0,
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
